// frontend/src/pages/AskAiPanel.js — NEW FILE (Phase E)
// Bottom-sheet chat panel for block-level / topic-level Ask AI.
// - Sends: question + topic/card/block ids + capped conversation history
// - Cap: 5 student questions per session; then input locks with a message and
//   a "Start new question" button that clears the history (fresh context)
// - Uses authFetch so the student token goes with the request

import React, { useState, useRef, useEffect } from 'react';
import { API_BASE, authFetch } from '../services/api.js';
import './studentfeed.css';

// ── CONFIRM THIS KEY: where your login stores the student object ──
// e.g. localStorage.setItem('studentData', JSON.stringify({student_id: 12, ...}))
const STUDENT_STORAGE_KEY = 'student';

export const getStudentId = () => {
    try {
        const raw = localStorage.getItem(STUDENT_STORAGE_KEY);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        return obj.student_id || obj.id || null;
    } catch {
        return null;
    }
};

const MAX_QUESTIONS = 5;

const LABELS = {
    en: {
        title: 'Ask AI',
        about: 'Asking about',
        placeholder: 'Type your question...',
        send: 'Send',
        thinking: 'Thinking...',
        limit: `You have asked ${MAX_QUESTIONS} questions in this session. Please start a new question so the answers stay focused.`,
        newQuestion: 'Start new question',
        error: 'Could not get an answer. Please try again.',
    },
    hi: {
        title: 'AI से पूछें',
        about: 'विषय',
        placeholder: 'अपना प्रश्न लिखें...',
        send: 'भेजें',
        thinking: 'सोच रहा है...',
        limit: `इस सत्र में आप ${MAX_QUESTIONS} प्रश्न पूछ चुके हैं। बेहतर उत्तर के लिए कृपया नया प्रश्न शुरू करें।`,
        newQuestion: 'नया प्रश्न शुरू करें',
        error: 'उत्तर नहीं मिल सका। कृपया दोबारा कोशिश करें।',
    },
};

const AskAiPanel = ({ topicId, topicName, card, block, language = 'en', onClose }) => {
    const t = LABELS[language] || LABELS.en;
    const [messages, setMessages] = useState([]);   // [{sender: 'student'|'ai', text}]
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const bodyRef = useRef(null);

    const questionCount = messages.filter((m) => m.sender === 'student').length;
    const limitReached = questionCount >= MAX_QUESTIONS;

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [messages, busy]);

    const send = async () => {
        const question = input.trim();
        if (!question || busy || limitReached) return;
        setInput('');
        const nextMessages = [...messages, { sender: 'student', text: question }];
        setMessages(nextMessages);
        setBusy(true);
        try {
            const res = await authFetch(`${API_BASE}/ask-ai/block-doubt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    question,
                    language,
                    topic_id: topicId,
                    card_type: card ? card.card_type : null,
                    card_id: card ? card.card_id : null,
                    block_id: block ? (block.rich_content_id || block.block_id || block.card_id || block.id || null) : null,
                    student_id: getStudentId(),
                    conversation_history: messages.slice(-10),
                }),
            });
            if (!res.ok) throw new Error('failed');
            const data = await res.json();
            setMessages((prev) => [...prev, { sender: 'ai', text: data.answer }]);
        } catch {
            setMessages((prev) => [...prev, { sender: 'ai', text: t.error }]);
        } finally {
            setBusy(false);
        }
    };

    const aboutLabel = block
        ? `${block.block_type}`
        : (card ? '' : topicName);

    return (
        <div className="aai-chat-window">
            <div className="aai-chat-header">
                <span className="aai-chat-title">✦ {t.title}</span>
                <button className="aai-chat-close" onClick={onClose} aria-label="Close">✕</button>
            </div>

            <div className="aai-chat-messages" ref={bodyRef}>
                {messages.length === 0 && (
                    <div className="aai-chat-welcome">{t.placeholder}</div>
                )}
                {messages.map((m, i) => (
                    <div key={i} className={`aai-chat-msg ${m.sender === 'student' ? 'aai-chat-msg-user' : 'aai-chat-msg-bot'}`}>
                        <div className="aai-chat-msg-content">{m.text}</div>
                    </div>
                ))}
                {busy && (
                    <div className="aai-chat-msg aai-chat-msg-bot">
                        <div className="aai-chat-msg-content aai-chat-thinking">{t.thinking}</div>
                    </div>
                )}
            </div>

            <div className="aai-chat-input-area">
                {limitReached && (
                    <div className="aai-chat-limit">
                        <span>{t.limit}</span>
                        <button onClick={() => setMessages([])}>{t.newQuestion}</button>
                    </div>
                )}
                <div className="aai-chat-input-row">
                    <textarea
                        value={input}
                        placeholder={t.placeholder}
                        disabled={busy || limitReached}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                        className="aai-chat-textarea"
                        rows={2}
                    />
                    <button onClick={send} disabled={busy || limitReached || !input.trim()} className="aai-chat-send">➤</button>
                </div>
            </div>
        </div>
    );
};

export default AskAiPanel;